import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContainer, Heading, Divider, Content, ButtonGroup, Button } from "@react-spectrum/s2";
import { DataTable, ColumnDefinition } from './DataTable';
export interface UnselectUsageRow {
    key: string;
    cells: (string | number)[];
}
export interface UnselectUsageDialogProps {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    title: string;
    description: string;
    columnLabels: string[];
    rows: UnselectUsageRow[];
    onClose: () => void;
}
/**
 * Blocking, informational dialog: the backend hard-rejects an allowlist
 * removal for anything still referenced by an active domain (409), so this
 * is never a "confirm anyway" step — there's no Proceed button. It only
 * explains which of the unselected configurations/models are still in use
 * (and where) so the actor knows what to re-check before Save can succeed.
 *
 * Reuses DataTable (rather than a bespoke TableView) — a raw TableView
 * rendered fresh inside a Dialog's first paint hits react-aria's
 * virtualizer with an unbounded/zero-height container and throws
 * (`getReusableView` on an undefined parent view) before layout settles;
 * DataTable already carries the minHeight/maxHeight + other props every
 * other table in this app relies on to avoid that.
 */ export const UnselectUsageDialog: React.FC<UnselectUsageDialogProps> = ({ isOpen, onOpenChange, title, description, columnLabels, rows, onClose })=>{
    const { t } = useTranslation();
    const columns: ColumnDefinition<UnselectUsageRow>[] = useMemo(()=>columnLabels.map((label, index)=>({
                key: String(index),
                label,
                isRowHeader: index === 0,
                render: (row: UnselectUsageRow)=><>{row.cells[index]}</>
            })), [
        columnLabels
    ]);
    if (!isOpen) return null;
    return (<DialogContainer onDismiss={()=>onOpenChange(false)}>
      <Dialog size="L">
        <Heading slot="title">{title}</Heading>
        <Divider/>
        <Content>
          <span>{description}</span>
          <div className={" JC171 FDvO7ld171"}>
            <DataTable<UnselectUsageRow> ariaLabel={title} items={rows} columns={columns} getRowKey={(row)=>row.key} density="regular" maxHeight="size-3000" emptyStateTitle={t('components.unselectUsageDialog.emptyState')}/>
          </div>
        </Content>
        <ButtonGroup>
          <Button variant="accent" onPress={onClose}>{t('primitives.close')}</Button>
        </ButtonGroup>
      </Dialog>
    </DialogContainer>);
};

import "macro-47c01958084e83492f5de72cfc9fbaab196744ad52057ac1f0946338782c7f81.css";